import {
  Box,
  Icon,
  Separator,
  useElementSize,
  useIsMobile,
} from "@salestrak/ui";
import * as React from "react";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useModuleRail } from "./module-rail-context";
import { MoreModulesTile } from "./more-modules-tile";
import { RailTile } from "./rail-tile";
import type { ResolvedRailTile } from "#app/lib/hooks/use-nav-preferences";
import { useSidebarNav } from "#app/components/navigation/use-sidebar-nav";
import { useInboxUnreadCount } from "#app/hooks/inbox/use-inbox-unread-count";
import { useAppData } from "#app/lib/data";
import { useWorkflowOrder } from "#app/lib/hooks/use-workflow-order";
import {
  useExpandSidebar,
  useSidebarCollapsed,
} from "#app/lib/hooks/use-sidebar-collapsed";

/**
 * ModuleRail — narrow vertical navigation rail (far-left column of the shell).
 *
 * Tile order + visibility come from the user's `railTiles` preference,
 * resolved against the static registry in `useNavPreferences` and shared via
 * `ModuleRailContext`. Hidden tiles move to the More popup. "Invite" is
 * permission-gated and opens InviteMembersDialog.
 */

// Rail capacity is measured, not capped: the rail tracks its own height and
// the (always-rendered) More tile's height, then fits as many shown tiles as
// the column actually holds. Shown tiles past the fit — and the Invite tile
// when even it no longer fits — overflow into the More popup, exactly like
// user-hidden tiles. The More tile itself always renders.
//
// Geometry the fit math can't measure live: the column's `gap="sm"`, the
// divider block before More (1px line + xs padding), and the collapsed-pane
// expand control (a 36px tile-style chip). The column gap is mode-dependent —
// `xs` (4px) compact, `md` (16px) expanded — so it's computed at render
// (`railGap`). Keep in sync with the JSX below.
const RAIL_DIVIDER_HEIGHT = 9;
const RAIL_EXPAND_HEIGHT = 36;

export function ModuleRail() {
  const isMobile = useIsMobile();
  // Invite visibility is grant-driven (role_invite_grant) — show when the
  // actor can invite into at least one role, NOT a permission key. Mirrors
  // the Members page and InviteMembersDialog gating.
  const canInvite = useCanInvite();
  const closeSidebar = useCloseSidebarIfMobile();
  const { activeTileId, keepDrawerOpenOnRailNav, compact, railTiles } = useModuleRail();
  const { navigate, isActive } = useSidebarNav();
  const inboxUnreadCount = useInboxUnreadCount();
  const { workflows } = useAppData();
  const { sortedWorkflows } = useWorkflowOrder(workflows);
  const firstWorkflowId = sortedWorkflows[0]?.workflowId;
  const [inviteOpen, setInviteOpen] = React.useState(false);
  // `useSidebarCollapsed` is already false on mobile (there, `open=false`
  // means the drawer is dismissed, not the pane collapsed), which is what the
  // rail wants in both places it reads this.
  const submenuCollapsed = useSidebarCollapsed();
  const expandSidebar = useExpandSidebar();

  // Live measurements driving the overflow fit: the rail column's height and
  // one tile's height (taken from the always-rendered More tile, so it tracks
  // compact mode and font changes for free). Zero = not measured yet.
  const { ref: railRef, size: railSize } = useElementSize<HTMLDivElement>();
  const { ref: tileSlotRef, size: tileSlotSize } =
    useElementSize<HTMLDivElement>();
  const railHeight = railSize.height || null;
  const tileHeight = tileSlotSize.height || null;

  // Rail top in viewport coords — the More popup's collision boundary, so it
  // never floats above the rail's top edge on short screens. Re-read whenever
  // the rail resizes.
  const [railTop, setRailTop] = React.useState(0);
  React.useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const top = Math.max(0, Math.floor(el.getBoundingClientRect().top));
    setRailTop((prev) => (prev === top ? prev : top));
  }, [railRef, railHeight]);

  const shownTiles = railTiles.filter((tile) => tile.show);

  // The Home tile navigates to the workspace root (`tile.route`), which owns
  // the landing decision: with WIDGETS on it redirects to the user's
  // configured default view (`preferences.defaultRoute` — Inbox by default),
  // and with it off it renders the legacy Home dashboard. No per-tile
  // override here — a hardcoded destination would silently defeat the
  // default-view preference.
  //
  // The Workflows tile deep-links straight to the user's first workflow's board
  // for fast access; with no visible workflows it falls back to `tile.route`
  // (`/workflows`, the roster page listing every workflow).
  const resolveRoute = (tile: ResolvedRailTile) => {
    if (tile.id === "workflows" && firstWorkflowId) return `/workflow/${firstWorkflowId}`;
    return tile.route;
  };

  // Pressing a tile just navigates — the active tile (and with it the
  // module menu, resolved in the workspace layout) derives from the route.
  // Workspace prefixing is handled by useSidebarNav. Switching modules keeps
  // the mobile drawer open so the user can pick from the new module menu —
  // flag the imminent route change, but only when it will actually change, to
  // avoid leaving the flag set. Tiles without a module menu give the user
  // nothing to pick from, so close the drawer instead (no-op on desktop) —
  // even when the route doesn't change. Shared with the More popup's overflow
  // rows so hidden tiles behave identically.
  const handleTilePress = (tile: ResolvedRailTile) => {
    const route = resolveRoute(tile);
    if (tile.hasSubmenu ?? true) {
      if (!isActive(route)) {
        keepDrawerOpenOnRailNav.current = true;
      }
    } else {
      closeSidebar();
    }
    navigate(route);
  };

  // Desktop only: when the module-menu pane was collapsed (the ModuleMenu
  // header's collapse control), show an expand control at the top of the rail.
  // On mobile the rail lives inside the drawer, so the collapsed state never
  // shows a rail to host it. Tiles without a module menu (e.g. map) are
  // excluded: there is nothing to expand into.
  const activeTileHasSubmenu = railTiles.find((tile) => tile.id === activeTileId)?.hasSubmenu ?? true;
  const showExpand = !isMobile && submenuCollapsed && activeTileHasSubmenu;

  // rail paddingTop (compact "sm" = 8, expanded "md" = 16) + paddingBottom ("sm" = 8)
  const padY = (compact ? 8 : 16) + 8;
  // Column gap: tight when compact (icon-only), roomier with labels.
  const railGap = compact ? 4 : 16;

  // Floor: Home + divider + More must always fit inside the rail's border.
  // Below this the column stops shrinking and overflows the viewport instead
  // of pushing the More tile outside the rail box — the least-bad failure
  // for absurdly short windows. (~60 ≈ a normal tile until measured.)
  const minRailHeight =
    padY + 2 * (tileHeight ?? 60) + RAIL_DIVIDER_HEIGHT + 3 * railGap;

  // Fit: how many shown tiles (and whether Invite) the measured column holds.
  // Invite cedes its slot first — nav tiles take priority — then shown tiles
  // drop from the end into the More popup. Home (always first) never drops.
  // Before both measurements land (first frame) everything renders.
  let visibleCount = shownTiles.length;
  let inviteInRail = canInvite;
  if (railHeight != null && tileHeight != null) {
    const fits = (count: number, withInvite: boolean) => {
      // Children: [expand?] tiles… divider More spacer [invite?] — the
      // zero-height flex spacer still contributes a gap slot.
      const children = (showExpand ? 1 : 0) + count + 3 + (withInvite ? 1 : 0);
      const height =
        padY +
        (showExpand ? RAIL_EXPAND_HEIGHT : 0) +
        (count + 1 + (withInvite ? 1 : 0)) * tileHeight +
        RAIL_DIVIDER_HEIGHT +
        railGap * (children - 1);
      return height <= railHeight;
    };
    inviteInRail = canInvite && fits(shownTiles.length, true);
    if (!inviteInRail) {
      while (visibleCount > 1 && !fits(visibleCount, false)) {
        visibleCount -= 1;
      }
    }
  }

  const visibleTiles = shownTiles.slice(0, visibleCount);
  // Fit-overflowed tiles first, then the user-hidden ones.
  const overflowTiles = [...shownTiles.slice(visibleCount), ...railTiles.filter((tile) => !tile.show)];

  return (
    <Box
      surface="sidebar"
      align="center"
      direction="column"
      fill="flex"
      gap={compact ? "xs" : "md"}
      minHeight={minRailHeight}
      paddingBottom="sm"
      // No horizontal padding: gives labels the full rail width so "Workflows"
      // fits. Icons are centered so dropping the padding doesn't shift them.
      paddingX="none"
      // Expanded: `md` top so the Home tile's top lines up with the ModuleMenu
      // title (its header uses the same top pad). Compact: no module menu to
      // align to, so `sm` (pt-2). `sm` bottom trims dead space under the rail's
      // pinned items.
      paddingTop={compact ? "sm" : "md"}
      ref={railRef}
      rounded="lg"
      // The surface prop supplies only the border COLOR; the hairline itself
      // must be asked for. Matches ContentPane and SidebarPane so the three
      // columns read as siblings.
      border
    >
      {/* Expand control — only while the module-menu pane is collapsed.
       * Mirrors the ModuleMenu header's hover-revealed collapse
       * (chevrons-left). */}
      {showExpand && (
        <Box
          align="center"
          aria-label="Expand sidebar"
          as="button"
          fill="width"
          onClick={expandSidebar}
        >
          {/* Mirror a rail tile's icon chip (36×36 rounded-xl, accent hover) so
            * the hover state matches the tiles below it. */}
          <Box
            align="center"
            height={36}
            hoverBackground
            justify="center"
            rounded="xl"
            width={36}
          >
            <Icon name="chevrons-right" size="sm" />
          </Box>
        </Box>
      )}

      {visibleTiles.map((tile, index) => {
        // Home shows the live inbox unread count. Suppress it while Home
        // is the active tile AND its module menu is visible — the module
        // menu's own Inbox row carries the count there. With the module menu
        // collapsed that row is hidden, so the rail badge stays on even on
        // Home.
        const badge =
          tile.id === "home" && (activeTileId !== "home" || submenuCollapsed) && inboxUnreadCount > 0
            ? String(inboxUnreadCount)
            : tile.badge;
        return (
          <RailTile
            active={tile.id === activeTileId}
            badge={badge}
            compact={compact}
            icon={tile.icon}
            key={`${tile.id}-${index}`}
            label={tile.label}
            onClick={() => handleTilePress(tile)}
          />
        );
      })}

      {/* Divider between the configurable tiles and the fixed "More" tile */}
      <Box align="center" fill="width" paddingY="xs">
        <Separator className="w-7" />
      </Box>

      {/* Measuring wrapper: the More tile is the one rail tile that ALWAYS
       * renders, so its height is the live per-tile slot height the fit
       * math uses (tracks compact mode and font changes automatically). */}
      <Box fill="width" ref={tileSlotRef}>
        <MoreModulesTile
          onInvitePress={
            canInvite && !inviteInRail
              ? () => {
                  closeSidebar();
                  setInviteOpen(true);
                }
              : undefined
          }
          onTilePress={handleTilePress}
          overflowTiles={overflowTiles}
          popoverTopInset={railTop}
        />
      </Box>

      {/* Spacer pushes the pinned bottom items down */}
      <Box fill="flex" />

      {/* Pinned bottom: Invite (permission-gated). When the column is too
       * short for its slot it moves into the More popup instead. */}
      {canInvite && inviteInRail && (
        <RailTile
          compact={compact}
          icon="user-plus"
          label="Invite"
          onClick={() => {
            closeSidebar();
            setInviteOpen(true);
          }}
        />
      )}
      {canInvite && <InviteMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} />}
    </Box>
  );
}
